import { Component, OnChanges, SimpleChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

import { Subscription } from 'rxjs';

import { TicketPayment } from '@unpispas/upp-data';
import { PlacePanel } from '@unpispas/upp-base';
import { ViewRenderer } from '@unpispas/upp-wdgt';

import { alertService } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';
import { i18nSupport } from '@unpispas/upp-data';
import { toastService } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { viewService } from '@unpispas/upp-base';

import { Ticket } from '@unpispas/upp-data';
import { MixedPayment } from '@unpispas/upp-data';
import { Pinpad } from '@unpispas/upp-data';
import { UppPinpadSessionComponent } from '@unpispas/feature/pinpad';
import { UppPinpadTicketComponent } from '@unpispas/feature/pinpad';

import { UserView } from '@unpispas/upp-data';
import { EmployeePermission } from '@unpispas/upp-data';
import { TicketUnits } from '@unpispas/upp-data';

import { PlaceView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
import { TicketProductView } from '@unpispas/upp-data';
import { DiscountView } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';
import { PayAccountView } from '@unpispas/upp-data';

import { printService } from '@unpispas/feature/print';
import { queueService } from '../../services/queue.service';

type EditActions = 'table' | 'account' | 'discount' | 'merge' | 'split' | 'return' | 'mixed' | 'partial' | 'edit' | 'invoice'

/************************************/
/* TICKET ACTIONS                   */
/************************************/

type TicketActions = 'create' | 'print' | 'cardvoucher' | 'reorder' | 'send' | 'email' | 'cash' | 'card' | 'mixed' | 'partial' | 'account' | 'discount' | 'table' | 'merge' | 'split' | 'open' | 'return' | 'tocash' | 'tocard' | 'cancel' | 'delete' | 'invoice';

class TicketControl {
    constructor(private ticket: TicketView | null, private change: ViewRenderer, private data: dataService, private panel: UppTicketEditComponent){
        // nothing to do
    }

    private _action: EditActions = 'edit';
    get action(): EditActions {
        return this._action;
    }

    set action(action: EditActions){
        if (this._action === action){
            return;
        }

        const _hadUnitsSelection = this._action === 'split' || this._action === 'return';
        const _willUseUnitsSelection = action === 'split' || action === 'return';

        if (this._action === 'return' && action !== 'return'){
            this._returnform = null;
            this._returnrefund = null;
        }

        this._action = action;

        if (_hadUnitsSelection || _willUseUnitsSelection){
            this.panel._unitsselection.clear();
        }

        this.panel.IsLocked = _willUseUnitsSelection;
        this.change.markForCheck();
    }    

    CanExecute(action: TicketActions){
        switch(action){
            case 'create': return this.CanCreate;
            case 'print': return this.CanPrint;
            case 'cardvoucher': return this.CanPinpadVoucher;
            case 'reorder': return this.CanReorder;
            case 'send': return this.CanSend;
            case 'email': return this.CanEmail;
            case 'cash': return !this.ticket?.IsPaid && this.CanCash;
            case 'card': return !this.ticket?.IsPaid && this.CanCard;
            case 'mixed': return this.IsOpened && this.CanMixed;
            case 'partial': return this.IsOpened && this.CanPartial;
            case 'account': return this.IsOpened && this.GrantedAccounts && this.CanChargeTicketToAccount;
            case 'discount': return this.IsOpened && this.GrantedInvite && this.CanDiscount;
            case 'table': return this.IsOpened && this.GrantedModify;
            case 'merge': return this.IsOpened && this.GrantedModify && this.CanMerge;
            case 'split': return this.IsOpened && this.GrantedModify && this.CanSplit;
            case 'open': return this.IsClosed && this.GrantedReopen && this.CanOpen;
            case 'return': return this.IsClosed && this.GrantedReturn && this.CanReturn;
            case 'tocash': return this.IsClosed && this.CanToCash;
            case 'tocard': return this.IsClosed && this.CanToCard;
            case 'cancel': return this.GrantedCancel && this.CanCancel;
            case 'delete': return this.GrantedCancel && this.CanCancel && this.CanDelete;
            case 'invoice': return !!this.ticket && !this.ticket.invceac;
        }
    }

    Execute(action: TicketActions){
        this.panel.ShowTicketOptions = false;        

        switch(action){
            case 'create': return this.TicketCreate();
            case 'reorder': return this.TicketReorder();
            case 'mixed': return this.TicketMixed();
            case 'partial': return this.TicketPartial();
            case 'account': return this.TicketAccount();
            case 'table': return this.TicketTable();
            case 'discount': return this.TicketDiscount();
            case 'merge': return this.TicketMerge();
            case 'split': return this.TicketSplit();
            case 'open': return this.OpenTicket();
            case 'tocash': return this.TicketToCash();
            case 'tocard': return this.TicketToCard();
            case 'return': return this.TicketReturn();
            case 'cancel': return this.TicketCancel();
            case 'delete': return this.TicketCancel(true);
            case 'invoice': return this.TicketInvoice();
            case 'email': return this.TicketEmail();
            case 'cardvoucher': return this.PinpadVoucher();
        }

        console.warn("[TICKET] Missing handler for action '" + action + "'")
    }

    CancelAction(){
        this._mergeinfo = null;
        this.MergeWarning = false;
        this._selectedaccount = null;
        this.action = 'edit';
    }

    /************************************/
    /* PRIVATE METHODS (EVALUATE)       */
    /************************************/      

    private get user(): UserView | null {
        return this.data.alives.get(this.data.user) as UserView;
    }

    private get place(): PlaceView | null {
        return this.ticket?.place || null;
    }

    private get qrcode(): QrCodeView | null {
        return this.ticket?.qrcode || null;
    }

    /* ticket status */

    private get IsOpened(): boolean{
        if (!this.ticket){
            return false;
        }

        return (!this.ticket.IsPaid && !this.ticket.IsToPay)
    }

    private get IsClosed(): boolean{
        if (!this.ticket){
            return false;
        }

        return (this.ticket.IsPaid || this.ticket.IsToPay)
    }

    /* user permissions */

    private get GrantedModify(): boolean {
        const user: UserView | null = this.user;
        if (user){
            return user.IsAllowedTo(EmployeePermission.CanModify)
        }
        return false;
    }

    private get GrantedInvite(): boolean {
        const user: UserView | null = this.user;
        if (user){
            return user.IsAllowedTo(EmployeePermission.CanCompliment)
        }
        return false;
    }

    private get GrantedReopen(): boolean {
        const user: UserView | null = this.user;
        if (user){
            return user.IsAllowedTo(EmployeePermission.CanReopen)
        }
        return false;
    }

    private get GrantedReturn(): boolean {
        const user: UserView | null = this.user;
        if (user){
            return user.IsAllowedTo(EmployeePermission.CanReturn)
        }
        return false;
    }

    private get GrantedCancel(): boolean {
        const user: UserView | null = this.user;
        if (user){
            return user.IsAllowedTo(EmployeePermission.CanCancel)
        }
        return false;
    }

    private get GrantedAccounts(): boolean {
        const user: UserView | null = this.user;
        if (user){
            return user.IsAllowedTo(EmployeePermission.CanAccounts);
        }
        return false;
    }

    /* ticket actions */

    private get CanCreate(): boolean {
        return this.IsOpened && !this.panel.IsNewTicket;
    }

    private get CanPrint(): boolean {
        if (!this.ticket){
            return false;
        }

        return this.ticket.CanPrint;
    }

    /** Card voucher reprint: own terminal + stored merchant/customer copy (ptf only). */
    private get CanPinpadVoucher(): boolean {
        return !!(this.ticket?.PinpadVoucher && this.place?.OwnPinpad);
    }

    /** Full kitchen/bar reprint as "copia de comanda" when there are prep lines. */
    private get CanReorder(): boolean {
        if (!this.ticket || this.ticket.IsCancelled){
            return false;
        }

        return this.ticket.ProductsToPrepare.length > 0;
    }

    private get CanSend(): boolean {
        return !!this.ticket?.CanSend;
    }

    private get CanCash(): boolean {
        return !!this.ticket?.CanCash;
    }

    private get CanCard(): boolean {
        return !!this.ticket?.CanCard;
    }

    private get CanMixed(): boolean {
        if (!this.ticket){
            return false;
        }

        return this.CanCash && this.CanCard;
    }

    private get CanPartial(): boolean {
        if (!this.ticket){
            return false;
        }

        return this.CanCash && this.CanCard
            && !this.ticket.ToInvoice
            && !this.ticket.IsCancel
            && this.ticket.due > 0;
    }

    private get CanChargeTicketToAccount(): boolean {
        if (!this.ticket){
            return false;
        }

        if (!this.panel.view.IsOnline){
            return false;
        }

        const _ticket = this.ticket;
        if (!_ticket.CanAccount){
            return false;
        }

        if (_ticket.empty){
            return false;
        }

        if (_ticket.ToInvoice){
            return false;
        }

        if (_ticket.HasPartial){
            return false;
        }

        const _accounts = this.place?.accounts;
        if (!_accounts || _accounts.length === 0){
            return false;
        }

        for (const _account of _accounts){
            if (!_account.IsBlocked){
                return true;
            }
        }

        return false;
    }

    private get CanDiscount(): boolean {
        if (!this.ticket || (this.panel.mode != 'EDIT')){
            return false;
        }

        return (this.TicketDiscounts.length > 0);
    }

    private get CanMerge(): boolean {   
        if (!this.ticket){
            return false;
        }

        return !this.ticket.empty && (this.MergeTickets.length > 0);
    }

    private get CanSplit(): boolean {
        if (!this.ticket){
            return false;
        }

        if (this.ticket.status === 'PR'){
            return false;
        }

        const ticketproducts = this.ticket?.products || [];
        if (ticketproducts.length == 0){
            return false;
        }

        return (ticketproducts.length > 1) || (ticketproducts[0].amount > 1);
    }

    get CanDoSplit(): boolean {
        const selected = this.panel.SelectedUnits;
        if (selected <= 0){
            return false;
        }

        return selected < this.panel.AvailableUnits;
    }

    get CanDoReturn(): boolean {
        return this.panel.SelectedUnits > 0;
    }

    private get CanOpen(): boolean {
        if (!this.ticket){
            return false;
        }

        return !this.ticket.invceac && !this.ticket.invoice;
    }

    private get CanReturn(): boolean {
        if (!this.ticket){
            return false;
        }

        if (this.ticket.invceac){
            return false;
        }

        return this.ticket.ProductsToPrepare.length > 0;
    }

    private get CanChangePayment(): boolean {
        if (!this.ticket?.payment){
            return false;
        }

        const _payment = this.ticket.payment;
        return _payment !== 'PAYLINE' && _payment !== 'PAYACCOUNT';
    }

    private get CanToCash(): boolean {
        if (!this.ticket || this.ticket.invceac || !this.CanChangePayment){
            return false;
        }

        if (this.ticket.payment === 'PAYCASH'){
            return false;
        }

        return this.place?.CanCash === true;
    }

    private get CanToCard(): boolean {
        if (!this.ticket || this.ticket.invceac || !this.CanChangePayment){
            return false;
        }

        if (this.ticket.payment === 'PAYCARD'){
            return false;
        }

        return this.place?.CanCard === true;
    }

    private get CanCancel(): boolean {
        if (!this.ticket){
            return false;
        }

        if (!this.panel.IsNewTicket){
            return (this.ticket.products.length > 0);
        }

        return false;
    }

    private get CanDelete(): boolean {
        if (!this.ticket?.lastchange){
            return false;
        }

        return !!(this.ticket.place?.SendBAIEnabled || this.ticket.place?.SendVFTEnabled);
    }

    /************************/
    /* ACTION: PRINT        */
    /************************/

    OnPrintClicked() {
        if (!this.ticket || !this.ticket.CanPrint){
            return;
        }

        this.ticket.DoPrint(false);
    }

    OnPrintPressed(pressed: boolean){
        console.log("TODO: pasa por aqui: OnPrintPressed", pressed);    // TODO
    }

    /************************/
    /* PINPAD: CARD VOUCHER */
    /************************/

    private _cardvouchering = false;

    /**
     * Reprints the stored card voucher on the own terminal. With both copies,
     * asks which one; with a single copy, prints it directly. Double-press guarded.
     */
    async PinpadVoucher(): Promise<void> {
        if (this._cardvouchering || !this.CanPinpadVoucher || !this.ticket){
            return;
        }

        const _row = this.ticket.PinpadVoucher;
        if (!_row){
            return;
        }

        const _receipts = _row.Receipts;
        const _hasMerchant = !!_receipts.merchant;
        const _hasCustomer = !!_receipts.customer;
        if (!_hasMerchant && !_hasCustomer){
            return;
        }

        this._cardvouchering = true;
        try {
            let _copy: 'merchant' | 'customer' | null = null;
            if (_hasMerchant && _hasCustomer){
                const _alert = this.panel.alertCtrl.alert({
                    header: this.panel.lang.tr('@ticket_action_cardvoucher'),
                    backdropDismiss: false,
                    buttons: [{
                        text: this.panel.lang.tr('@ticket_cardvoucher_merchant'),
                        handler: () => {
                            setTimeout(() => {
                                _alert.dismiss('merchant');
                            }, 100);
                            return false;
                        },
                    }, {
                        text: this.panel.lang.tr('@ticket_cardvoucher_customer'),
                        handler: () => {
                            setTimeout(() => {
                                _alert.dismiss('customer');
                            }, 100);
                            return false;
                        },
                    }, {
                        text: this.panel.lang.tr('@place_ticket_cancel'),
                        role: 'cancel',
                        handler: () => {
                            setTimeout(() => {
                                _alert.dismiss(null);
                            }, 100);
                            return false;
                        },
                    }],
                });

                await _alert.present();
                const _data = (await _alert.onDidDismiss())?.data;
                _copy = (_data === 'merchant' || _data === 'customer') ? _data : null;
            }
            else if (_hasMerchant){
                _copy = 'merchant';
            }
            else {
                _copy = 'customer';
            }

            if (!_copy){
                return;
            }

            await this.panel.queue.PrintVoucher(this.ticket, _copy);
        }
        finally {
            this._cardvouchering = false;
        }
    }

    /************************/
    /* ACTION: REORDER      */
    /************************/

    /**
     * Re-sends the full preparation slip to every order printer as a copy.
     * Relies on the edit panel's queue Watch so OnOrder has a consumer.
     */
    private TicketReorder(){
        if (!this.ticket || !this.CanReorder){
            return;
        }

        this.ticket.DoReorder();
    }

    /************************/
    /* ACTION: CREATE       */
    /************************/

    private async TicketCreate(){
        if (!this.ticket){
            return;
        }

        if (!this.panel.ContentDirty || await this.panel._ConfirmClose()){
            const _ticket = new Ticket(null, this.data);
            if (_ticket){
                _ticket.created = new Date();
                _ticket.updated = new Date();
                _ticket.place = this.data.place;
                _ticket.qrcode = this.ticket.qrcode?.object || null;            
                _ticket.session = this.data.session;
                _ticket.price = 0;
                _ticket.refund = 0;
                _ticket.taxes = 0;          // TODO (place taxes)            
                _ticket.status = 'PR';
            }
    
            this.panel.ToPanel.emit({
                main: 'cart',
                scnd: 'ticket',
                args: {
                    ticket: this.data.alives.get(_ticket) as TicketView,
                    mode: 'EDIT'
                }                
            });
        }        
    }

    /************************/
    /* ACTION: MIXED        */
    /************************/

    private _paymentform: FormGroup | null = null;
    
    private LoadPaymentForm(): FormGroup {
        const _cash = null;
        const _card = null;

        let _form: FormGroup | null = this._paymentform;
        if (_form == null){
            _form = new FormGroup({     
                cash: new FormControl(_cash, [
                    Validators.required
                ]),
                card: new FormControl(_card, [
                    Validators.required
                ])
            });
        }
        else {
            _form.patchValue({
                cash: _cash,
                card: _card
            });
        }
        this.change.markForCheck();
        
        _form.markAsPristine();
        setTimeout(() => {
            _form?.markAsPristine();
        }, 0)    
        
        return _form;            
    }

    private _payment_status_break = false;
    get PaymentForm(): FormGroup {
        if (this._paymentform == null){
            this._paymentform = this.LoadPaymentForm();

            if (this._payment_status_break){
                this._paymentform.statusChanges.subscribe(status => {
                    console.log('Form status changed:', status, 'Pristine:', this._paymentform?.pristine);
                });                
            }
        }
        return this._paymentform;        
    }

    TicketMixed(){
        this.action = 'mixed'
    }

    get MixedReady(): boolean {
        if (this.PaymentForm.valid && this.ticket){
            let _total = 0;

            _total += Number(this.PaymentForm.get('cash')?.value);
            _total += Number(this.PaymentForm.get('card')?.value);

            const _expected = this.ticket.HasPartial ? this.ticket.due : this.ticket.price;
            return (_total == _expected);
        }

        return false;
    }

    OnMixedPayment(){
        if (!this.ticket){
            return;
        }

        const cash = Number(this.PaymentForm.get('cash')?.value) || 0;
        const card = Number(this.PaymentForm.get('card')?.value) || 0;

        if (this.ticket.HasPartial){
            this._AddPartial('PAYCASH', cash);
            this._AddPartial('PAYCARD', card);
            this._EndPartial();
            return;
        }

        let _ticketcash: MixedPayment | null = null;
        let _ticketcard: MixedPayment | null = null;

        for (const _mixedpayment of this.ticket.mixed){
            switch(_mixedpayment.payment){
                case 'PAYCASH':
                    _ticketcash = _mixedpayment;
                    break;
                case 'PAYCARD':
                    _ticketcard = _mixedpayment;
                    break;
            }
        }

        _ticketcash = _ticketcash || new MixedPayment(null, this.data);
        _ticketcard = _ticketcard || new MixedPayment(null, this.data);

        if (_ticketcash){
            _ticketcash.payment = 'PAYCASH';
            _ticketcash.amount = cash;
            _ticketcash.ticket = this.ticket.object;
        }

        if (_ticketcard){
            _ticketcard.payment = 'PAYCARD';
            _ticketcard.amount = card;
            _ticketcard.ticket = this.ticket.object;
        }

        if (_ticketcash && _ticketcard){
            this.ticket.AddMixed(_ticketcash);
            this.ticket.AddMixed(_ticketcard);

            this.panel.ContentDirty = false;
            void this.panel.OnCommit('PAYMIXED');
        }

        this.CancelAction();
    }    

    /************************/
    /* ACTION: PARTIAL      */
    /************************/

    private _partialform: FormGroup | null = null;

    private LoadPartialForm(): FormGroup {
        const _cash = null;
        const _card = null;

        let _form: FormGroup | null = this._partialform;
        if (_form == null){
            _form = new FormGroup({
                cash: new FormControl(_cash, [
                    Validators.required
                ]),
                card: new FormControl(_card, [
                    Validators.required
                ])
            });
        }
        else {
            _form.patchValue({
                cash: _cash,
                card: _card
            });
        }
        this.change.markForCheck();

        _form.markAsPristine();
        setTimeout(() => {
            _form?.markAsPristine();
        }, 0);

        return _form;
    }

    get PartialForm(): FormGroup {
        if (this._partialform == null){
            this._partialform = this.LoadPartialForm();
        }
        return this._partialform;
    }

    TicketPartial(){
        this._partialform = this.LoadPartialForm();
        this.action = 'partial';
    }

    get PartialReady(): boolean {
        if (this.PartialForm.valid && this.ticket){
            const cash = Number(this.PartialForm.get('cash')?.value) || 0;
            const card = Number(this.PartialForm.get('card')?.value) || 0;
            const total = Math.round((cash + card) * 100) / 100;
            return total > 0 && total <= this.ticket.due;
        }
        return false;
    }

    /**
     * Registers a partial cash/card payment. Completes the ticket when the due amount is covered;
     * otherwise persists without navigating away from the edit panel.
     */
    OnPartialPayment(){
        if (!this.ticket){
            return;
        }

        this._AddPartial('PAYCASH', Number(this.PartialForm.get('cash')?.value) || 0);
        this._AddPartial('PAYCARD', Number(this.PartialForm.get('card')?.value) || 0);

        this._EndPartial();
    }

    /** Registers one method/amount advance via `OnPartial`; skips it once the ticket is already paid. */
    private _AddPartial(payment: TicketPayment, amount: number): void {
        if (!this.ticket || amount <= 0 || this.ticket.IsPaid){
            return;
        }

        this.ticket.OnPartial(payment, amount);
    }

    /**
     * After OnPartial advances: close the panel when paid; otherwise persist and stay in edit.
     */
    private _EndPartial(): void {
        if (!this.ticket){
            return;
        }

        this.panel.ContentDirty = false;

        if (this.ticket.IsPaid){
            void this.panel.OnCommit(this.ticket.payment);
        }
        else {
            this.ticket.OnCommit(null);
        }

        this.CancelAction();
    }

    /************************/
    /* ACTION: DISCOUNTS    */
    /************************/

    private _discounts: Array <DiscountView> | null = null;
    get TicketDiscounts(): Array <DiscountView> {
        if (this._discounts === null){
            const _discounts: Array <DiscountView> = [];
        
            if (this.place && this.ticket){
                for (const _discount of this.place.discounts){
                    if (_discount.AppliesTo(this.ticket)){
                        _discounts.push(_discount);
                    }
                }
            }

            this._discounts = _discounts;

            // keep the value during this event loop
            Promise.resolve().then(() => {
                this._discounts = null;
            });               
        }

        return this._discounts;
    }

    TicketDiscount(){
        this.action = 'discount';
    }

    OnApplyDiscount(discount: DiscountView){
        if (this.ticket){
            this.ticket.AddDiscount(discount);
        }

        this.CancelAction();
    }

    /************************/
    /* ACTION: INVOICE      */
    /************************/

    get InvoiceWarning(): boolean {
        return this.ticket?.pendpay != null || this._action === 'invoice';
    }

    private get CanEmail(): boolean {
        return String(this.ticket?.invoice?.client?.email || '').trim().length > 0;
    }

    TicketInvoice(){
        this.action = 'invoice';
    }

    async OnInvoiceAccept(): Promise<void> {
        if (!this.ticket){
            return;
        }

        await this.panel.OnCommit(null);
    }

    private _mailing = false;

    async TicketEmail(): Promise<void> {
        if (this._mailing || !this.ticket || !this.CanEmail) {
            return;
        }

        const _email = String(this.ticket.invoice?.client?.email || '').trim();
        if (!_email) {
            return;
        }

        this._mailing = true;
        void this.panel.toast.ShowWait();
        let _prepared: { element: HTMLElement; release: () => void } | null = null;
        try {
            _prepared = await this.panel.queue.PrepareReceiptElement(this.ticket);
            if (!_prepared) {
                console.error('[TICKET EMAIL] PrepareReceiptElement returned null');
                this.panel.toast.ShowAlert('danger', this.panel.lang.tr('@service_request_error_1'));
                return;
            }

            const _series = String(this.ticket.series || '');
            const _number = String(this.ticket.invceno ?? '');
            const _invoiceName = (_series || _number)
                ? (_series + _number + '.pdf')
                : 'invoice.pdf';

            const _code = await this.panel.printer.SendInvoiceEmail({
                element: _prepared.element,
                lang: this.panel.lang.GetLanguage().toLowerCase(),
                recipient: _email,
                invoiceName: _invoiceName,
                ticketObjid: this.ticket.objid,
            });

            if (_code === 0) {
                const _alert = this.panel.alertCtrl.alert({
                    header: this.panel.lang.tr('@place_ticket_sent_invoice'),
                    message: this.panel.lang.tr('@place_ticket_sent_message', [ _email ]),
                    backdropDismiss: true,
                    buttons: [{
                        text: this.panel.lang.tr('@ok'),
                        handler: () => {
                            setTimeout(() => { _alert.dismiss(false); }, 100);
                            return false;
                        },
                    }],
                });
                await _alert.present();
            }
            else {
                console.error('[TICKET EMAIL] Invoice email send failed with errorcode', _code);
                this.panel.toast.ShowAlert('danger', this.panel.lang.tr('@service_request_error_' + _code));
            }
        }
        catch (err) {
            console.error('[TICKET EMAIL] Invoice email send threw', err);
            this.panel.toast.ShowAlert('danger', this.panel.lang.tr('@service_request_error_1'));
        }
        finally {
            if (_prepared) {
                _prepared.release();
            }
            this.panel.toast.HideWait();
            this._mailing = false;
        }
    }

    /************************/
    /* ACTION: ACCOUNT PAY  */
    /************************/

    private _selectedaccount: PayAccountView | null = null;

    get SelectedAccount(): PayAccountView | null {
        return this._selectedaccount;
    }

    set SelectedAccount(value: PayAccountView | null) {
        this._selectedaccount = value;
        this.change.markForCheck();
    }

    TicketAccount(){
        this._selectedaccount = null;
        this.action = 'account';
    }

    OnAccountSelected(account: PayAccountView | null): void {
        if (account?.IsBlocked){
            return;
        }

        this._selectedaccount = account;
        this.change.markForCheck();
    }

    OnPayAccount(): void {
        if (!this.ticket || !this._selectedaccount){
            return;
        }

        if (!this.panel.view.IsOnline){
            return;
        }

        if (!this.GrantedAccounts || !this.ticket.CanAccount){
            return;
        }

        if (this.ticket.empty){
            return;
        }

        if (this.ticket.HasPartial){
            return;
        }

        if (this._selectedaccount.IsBlocked){
            return;
        }

        this.ticket.account = this._selectedaccount;
        this.panel.ContentDirty = false;
        void this.panel.OnCommit('PAYACCOUNT');
        this.CancelAction();
    }

    /************************/
    /* ACTION: TABLE        */
    /************************/

    private _qrcodes: Array <QrCodeView> | null = null;
    get TicketTables(): Array <QrCodeView> {
        if (this._qrcodes === null){
            const _qrcodes: Array <QrCodeView> = [];
        
            if (this.place && this.ticket){
                for (const _qrcode of this.place.tables){
                    if (this.ticket.qrcode != _qrcode){
                        _qrcodes.push(_qrcode);
                    }
                }
            }

            this._qrcodes = _qrcodes;

            // keep the value during this event loop
            Promise.resolve().then(() => {
                this._qrcodes = null;
            });               
        }

        return this._qrcodes;
    }

    TicketTable(){
        this.action = 'table';
    }

    OnApplyTable(qrcode: QrCodeView){
        if (!this.ticket){
            return;
        }

        this.panel.ContentDirty = false;   
        this.ticket.qrcode = qrcode;
        this.ticket.OnCommit(null);  

        this.CancelAction();
    }

    /************************/
    /* ACTION: MERGE        */
    /************************/

    private _mergetickets: Array <TicketView> | null = null;
    get MergeTickets(): Array <TicketView> {
        if (!this.ticket || !this.qrcode){
            return [];
        }

        if (this._mergetickets === null){
            const _tickets: Array <TicketView> = [];
            for (const ticket of this.qrcode.tickets){
                if (this.ticket.IsCopyOf(ticket.object)){
                    continue;   // cannot merge with myself
                }

                if (ticket.IsRecent && ticket.IsOpened){
                    _tickets.push(ticket);
                }
            }

            this._mergetickets = _tickets;

            // keep the value during this event loop
            Promise.resolve().then(() => {
                this._mergetickets = null;
            });                 
        }

        return this._mergetickets;
    }

    private TicketMerge(){
        this._mergeinfo = null;
        this.MergeWarning = false;
        this.MergeBlocked = false;
        this.action = 'merge';
    }

    private _mergewarning = false;
    get MergeWarning(): boolean {
        return this._mergewarning;
    }

    set MergeWarning(value: boolean) {
        this._mergewarning = value;
        this.change.markForCheck();
    }

    private _mergeblocked = false;
    get MergeBlocked(): boolean {
        return this._mergeblocked;
    }

    set MergeBlocked(value: boolean){
        this._mergeblocked = value;
        this.change.markForCheck();
    }

    private _mergeinfo: { target: TicketView; dscwrn: boolean } | null = null;
    get MergeInfo(): { target: TicketView; dscwrn: boolean } | null {
        return this._mergeinfo;
    }

    OnTicketMerge(target: TicketView){
        if (!this.ticket || !target){
            return;
        }

        const warning = this._CheckMergeConficts(this.ticket, target);
        const blocked = !warning && !this._CanMergeDiscounts(this.ticket, target);
        this._mergeinfo = {
            target,
            dscwrn: warning
        };
        this.MergeWarning = warning;
        this.MergeBlocked = blocked;
        this.change.markForCheck();
    }

    OnMergeCancel(): void {
        this._mergeinfo = null;
        this.MergeWarning = false;
        this.MergeBlocked = false;
    }

    async OnMergeCommit(): Promise <void> {
        if (!this.ticket || !this._mergeinfo){
            return;
        }

        const merge = this._mergeinfo.target;
        const warning = this._mergeinfo.dscwrn;
        if (!warning && !this._CanMergeDiscounts(this.ticket, merge)){
            this.MergeBlocked = true;
            return;
        }

        this.ticket.OnMerge(merge, { allowDiscountLoss: warning });

        const transaction = this.data.transaction;
        let flushed = true;
        if (transaction){
            await this.ticket.OnCommit(null, transaction);
            await merge.OnCommit(null, transaction);
            flushed = await transaction.flush(true);
        }

        this._mergeinfo = null;
        this.MergeWarning = false;
        this.MergeBlocked = false;

        if (!flushed){
            this.change.markForCheck();
            return;
        }

        this.panel.ContentDirty = false;
        this.action = 'edit';
    }

    private _CheckMergeConficts(ticket: TicketView, merge: TicketView): boolean {
        const discounts = new Set <DiscountView> ();

        for (const ticketdiscount of ticket.discounts){
            if (ticketdiscount.discount){
                discounts.add(ticketdiscount.discount);
            }
        }

        for (const ticketdiscount of merge.discounts){
            if (ticketdiscount.discount){
                discounts.add(ticketdiscount.discount);
            }
        }

        if (discounts.size < 2){
            return false;
        }

        for (const discount of discounts){
            if (discount.cumulative === false){
                return true;
            }
        }

        return false;
    }

    private _CanMergeDiscounts(ticket: TicketView, merge: TicketView): boolean {
        for (const ticketdiscount of merge.discounts){
            const discount: DiscountView | null = ticketdiscount.discount;
            if (!discount || !discount.AppliesTo(ticket)){
                return false;
            }
        }

        return true;
    }

    /************************/
    /* ACTION: SPLIT        */
    /************************/

    private TicketSplit(){
        this.action = 'split';
    }

    async OnTicketSplit(): Promise <void> {
        if (!this.ticket){
            return;
        }

        if (!this.CanDoSplit){
            throw new Error('[TICKET] Split action requires a partial product selection');
        }

        const selections: Array <TicketUnits> = this.panel.UnitsSelections;
        const split = this.ticket.OnSplit(selections);

        const transaction = this.data.transaction;
        if (transaction){
            await this.ticket.OnCommit(null, transaction);
            await split.OnCommit(null, transaction);
            await transaction.flush();
        }

        this.panel.ContentDirty = false;
        this.action = 'edit';

        this.panel.ToPanel.emit({
            main: 'cart',
            scnd: 'ticket',
            args: {
                ticket: split,
                mode: 'EDIT'
            }
        });
    }

    /************************/
    /* ACTION: TOCASH      */
    /************************/

    private async TicketToCash(){
        if (!this.ticket){
            return;
        }

        this.panel.ContentDirty = false;   
        this.ticket.account = null;
        await this.ticket.OnCommit('PAYCASH');
        this.change.markForCheck();
    }

    /************************/
    /* ACTION: TOCARD       */
    /************************/

    private async TicketToCard(){
        if (!this.ticket){
            return;
        }

        this.panel.ContentDirty = false;   
        this.ticket.account = null;
        await this.ticket.OnCommit('PAYCARD');
        this.change.markForCheck();
    }  

    /************************/
    /* ACTION: REOPEN       */
    /************************/

    async OpenTicket(){
        if (await this.panel._ConfirmOpen()){
            if (!this.ticket){
                return;
            }

            this.ticket.OnOpen();

            this.panel.ContentDirty = false;   
            this.ticket.OnCommit(null);
        }
    }

    /************************/
    /* ACTION: RETURN       */
    /************************/

    private TicketReturn(){
        this.action = 'return';
    }

    private _returnform: FormGroup | null = null;
    private _returnrefund: 'PAYCASH' | 'PAYCARD' | 'PAYMIXED' | null = null;
    private _syncingReturnAmounts = false;

    private LoadReturnForm(): FormGroup {
        const _cash = null;
        const _card = null;

        let _form: FormGroup | null = this._returnform;
        if (_form == null){
            _form = new FormGroup({
                cash: new FormControl(_cash, [
                    Validators.required,
                    this.ValidateReturnPrice.bind(this)
                ]),
                card: new FormControl(_card, [
                    Validators.required,
                    this.ValidateReturnPrice.bind(this)
                ])
            });
        }
        else {
            _form.patchValue({
                cash: _cash,
                card: _card
            });
        }

        this.change.markForCheck();

        _form.markAsPristine();
        setTimeout(() => {
            _form?.markAsPristine();
        }, 0);

        return _form;
    }

    get ReturnForm(): FormGroup {
        if (this._returnform == null){
            this._returnform = this.LoadReturnForm();
        }
        return this._returnform;
    }

    get ReturnPrice(): number {
        if (!this.ticket){
            return 0;
        }

        let _total = 0;
        const _paid = this.ticket.IsPaid && !this.ticket.IsToPay;

        for (const selection of this.panel.UnitsSelections){
            if (selection.units <= 0){
                continue;
            }

            const _charge = selection.product.charge || 0;
            if (_paid && _charge <= 0){
                continue;
            }

            _total += _charge * selection.units;
        }

        return Math.round(_total * 100) / 100;
    }

    get CanReturnFullCard(): boolean {
        if (this.ReturnPrice <= 0){
            return false;
        }

        return this.SplitRefund().card >= this.ReturnPrice;
    }

    get CanReturnAnyCard(): boolean {
        return this.SplitRefund().card > 0;
    }

    /** False when place cash is not allowed; hides the cash/mixed return options. */
    get CanReturnCash(): boolean {
        return this.place?.CanCash === true;
    }

    get ReturnRefund(): 'PAYCASH' | 'PAYCARD' | 'PAYMIXED' | null {
        return this._returnrefund;
    }

    get ReturnReady(): boolean {
        if (!this.ticket){
            return true;
        }

        if (this.ReturnPrice === 0){
            return true;
        }

        if (!this._returnrefund){
            return false;
        }

        if (this._returnrefund === 'PAYCASH'){
            return Number(this.ReturnForm.get('cash')?.value) === this.ReturnPrice
                && Number(this.ReturnForm.get('card')?.value) === 0;
        }

        if (this._returnrefund === 'PAYCARD'){
            return Number(this.ReturnForm.get('card')?.value) === this.ReturnPrice
                && Number(this.ReturnForm.get('cash')?.value) === 0;
        }

        if (!this.ReturnForm.valid){
            return false;
        }

        let _total = 0;
        _total += Number(this.ReturnForm.get('cash')?.value);
        _total += Number(this.ReturnForm.get('card')?.value);

        return _total === this.ReturnPrice;
    }

    OnReturnSelectionChanged(): void {
        this._returnrefund = null;
        this._returnform = null;
        this.change.markForCheck();
    }

    OnReturnRefundChange(value: string | null | undefined): void {
        if (value !== 'PAYCASH' && value !== 'PAYCARD' && value !== 'PAYMIXED'){
            return;
        }

        if (value === 'PAYCASH' && !this.CanReturnCash){
            return;
        }

        if (value === 'PAYCARD' && !this.CanReturnFullCard){
            return;
        }

        if (value === 'PAYMIXED' && !(this.CanReturnAnyCard && this.CanReturnCash)){
            return;
        }

        this._returnrefund = value;
        const _price = this.ReturnPrice;
        const _form = this.ReturnForm;

        if (value === 'PAYCASH'){
            _form.patchValue({
                cash: parseFloat(_price.toString()).toFixed(2),
                card: parseFloat('0').toFixed(2),
            });
        }
        else if (value === 'PAYCARD'){
            _form.patchValue({
                cash: parseFloat('0').toFixed(2),
                card: parseFloat(_price.toString()).toFixed(2),
            });
        }
        else {
            _form.patchValue({
                cash: null,
                card: null,
            });
        }

        _form.updateValueAndValidity();
        this.change.markForCheck();
    }

    private SplitRefund(): { cash: number; card: number } {
        const _total = this.ReturnPrice;
        if (!this.ticket){
            return { cash: 0, card: 0 };
        }

        let _card = 0;
        switch (this.ticket.payment){
            case 'PAYCASH':
                break;
            case 'PAYCARD':
                _card = _total;
                break;
            case 'PAYMIXED':
                _card = Math.min(this.PayAmount('PAYCARD'), _total);
                break;
        }

        return { cash: _total, card: _card };
    }

    private ValidateReturnPrice(control: AbstractControl): ValidationErrors | null {
        const _price = Number(control.value);
        if (_price){
            const _split = this.SplitRefund();
            const _card = control.parent?.get('card');
            const _max = _card && control === _card ? _split.card : _split.cash;
            if ((_price < 0) || (_price > _max)){
                return { invalid: true };
            }
        }

        return null;
    }

    private PayAmount(payment: 'PAYCASH' | 'PAYCARD'): number {
        if (!this.ticket){
            return 0;
        }

        for (const _mixed of this.ticket.mixed){
            if (_mixed.payment === payment){
                return _mixed.amount || 0;
            }
        }

        return 0;
    }

    OnReturnCashChanged(): void {
        if (this._returnrefund !== 'PAYMIXED' || this._syncingReturnAmounts){
            return;
        }

        const _return = this.ReturnPrice;
        const _cardMax = this.SplitRefund().card;
        const _cash = this.ReturnForm.get('cash');
        const _card = this.ReturnForm.get('card');
        if (!_cash || !_card){
            return;
        }

        const _opts = { emitEvent: false };
        const _cashvalue = _cash.value !== null && _cash.value !== '' ? Number(_cash.value) : null;

        this._syncingReturnAmounts = true;
        try {
            if (_cashvalue === null || Number.isNaN(_cashvalue)){
                if (_card.value !== null){
                    _card.setValue(null, _opts);
                }
                return;
            }

            if (_cashvalue <= _return){
                const _cardvalue = _return - _cashvalue;
                if (_cardvalue <= _cardMax){
                    const _cardFormatted = _cardvalue.toFixed(2);
                    if (_card.value !== _cardFormatted){
                        _card.setValue(_cardFormatted, _opts);
                    }
                }
            }
        }
        finally {
            this._syncingReturnAmounts = false;
        }

        this.change.markForCheck();
    }

    OnReturnCardChanged(): void {
        if (this._returnrefund !== 'PAYMIXED' || this._syncingReturnAmounts){
            return;
        }

        const _return = this.ReturnPrice;
        const _cardMax = this.SplitRefund().card;
        const _cash = this.ReturnForm.get('cash');
        const _card = this.ReturnForm.get('card');
        if (!_cash || !_card){
            return;
        }

        const _opts = { emitEvent: false };
        const _cardvalue = _card.value !== null && _card.value !== '' ? Number(_card.value) : null;

        this._syncingReturnAmounts = true;
        try {
            if (_cardvalue === null || Number.isNaN(_cardvalue)){
                if (_cash.value !== null){
                    _cash.setValue(null, _opts);
                }
                return;
            }

            if (_cardvalue <= _cardMax){
                const _cashvalue = _return - _cardvalue;
                const _cashFormatted = _cashvalue.toFixed(2);
                if (_cash.value !== _cashFormatted){
                    _cash.setValue(_cashFormatted, _opts);
                }
            }
        }
        finally {
            this._syncingReturnAmounts = false;
        }

        this.change.markForCheck();
    }

    async OnTicketReturn(): Promise<void> {
        const _ticket = this.ticket;
        if (!_ticket){
            return;
        }

        if (!this.CanDoReturn){
            throw new Error('[TICKET] Return action requires selected products');
        }

        let _refund: { cash: number; card: number } | null = null;
        if (this.ReturnPrice > 0){
            if (!this.ReturnReady){
                return;
            }

            _refund = {
                cash: Number(this.ReturnForm.get('cash')?.value),
                card: Number(this.ReturnForm.get('card')?.value),
            };
        }

        if (_refund && _refund.card > 0 && this.place?.CardMethod === 'TERMINAL'){
            if (!(await this._doCardRefund(_ticket, _refund))){
                return;
            }
        }

        if (this.ticket !== _ticket){
            return;
        }

        _ticket.OnReturn(this.panel.UnitsSelections, _refund);
        this._returnform = null;
        this._returnrefund = null;
        this.action = 'edit';

        this.panel.ContentDirty = false;
        await _ticket.OnCommit(null);

        if (_refund && _refund.card > 0 && this.place?.CardMethod === 'TERMINAL'){
            const _row = _ticket.RefundToApply;
            if (_row){
                await _ticket.CloseRefund(_row);
            }
        }

        this.change.markForCheck();
    }

    /************************/
    /* PINPAD REFUND        */
    /************************/

    /**
     * Resolves origin/terminal and runs the pinpad refund session before registering the
     * return. Returns whether the caller should proceed (`false` = cancel / fail).
     */
    private async _doCardRefund(ticket: TicketView, refund: { cash: number; card: number }): Promise<boolean> {
        if (ticket.RefundCovers(refund.card)){
            this.panel.toast.ShowAlert('success', this.panel.lang.tr('@ticket_refund_registering_approved'), 6000);
            return true;
        }

        const _place = this.place;
        const _session = this.panel.pinpadSession;
        if (!_place || !_session) {
            return false;
        }

        // Origin from Ticket + terminal from Place (never stored on Place).
        const _origin = ticket.RefundOrigin(refund.card);
        let _terminal: Pinpad | null = null;
        if (_origin) {
            const _pad = _origin.pinpad as Pinpad | null;
            // Linked refund only on the sale terminal while it remains operable.
            _terminal = _pad?.IsValid ? _pad : null;
        }
        else if (_place.OwnPinpad) {
            _terminal = _place.OwnPinpad;
        }
        else if (_place.pinpads.length === 1) {
            _terminal = _place.pinpads[0];
        }

        const _result = await _session.Refund({
            ticket,
            place: _place,
            amount: refund.card,
            plan: { origin: _origin, terminal: _terminal },
        });

        if (_result.kind === 'CANCELLED') {
            return false;
        }

        if (_result.kind === 'MANUAL') {
            return true;
        }

        const _done = _result.done;
        if (_done.outcome === 'CLOSED') {
            return false;
        }

        if (_done.outcome === 'APPROVED' && !ticket.RefundCovers(refund.card)) {
            // The dialog only confirmed a previous attempt of a different amount.
            this.panel.toast.ShowAlert('warning', this.panel.lang.tr('@ticket_refund_unregistered'), 6000);
            return false;
        }

        return true;
    }

    /************************/
    /* ACTION: CANCEL       */
    /************************/

    async TicketCancel(iserror = false){
        if (await (iserror ? this.panel._ConfirmDelete() : this.panel._ConfirmCancel())){
            if (!this.ticket){
                return;
            }

            this.panel.ContentDirty = false;

            if (this.ticket.CanDiscard){
                this.ticket.OnDiscard();
                return;
            }
    
            this.ticket.iserror = iserror;
            this.ticket.OnCancel();
            this.ticket.OnCommit(null);
        }
    }
}

/************************************/
/* TICKET EDITION (MAIN)            */
/************************************/

@Component({
    selector: 'upp-ticket-edit',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-edit.html',
    styleUrls: [
      './upp-edit.scss'
    ],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]       
})
export class UppTicketEditComponent extends i18nSupport implements OnInit, OnChanges, OnDestroy {
    @Input() place: PlaceView | null = null;
    @Input() ticket: TicketView | null = null;
    @Input() mode: 'EDIT' | 'VIEW' = 'VIEW';
    @Input() action: 'send' | 'cash' | 'card' | null = null;

    @Output() ToPanel = new EventEmitter<PlacePanel>();
    @Output() OnLock = new EventEmitter<boolean>();

    /** Paytef session host (dialogs live here). Overridable in unit tests without TestBed. */
    @ViewChild(UppPinpadSessionComponent) pinpadSession: UppPinpadSessionComponent | null = null;

    /** Pending-card banner; refreshed after a CLOSED charge on the same open ticket. */
    @ViewChild(UppPinpadTicketComponent) pinpadTicket: UppPinpadTicketComponent | null = null;

    private _online_subscription: Subscription | null = null;
    private _ticketdata_subscription: Subscription | null = null; 
    private _ticketview_subscription: Subscription | null = null;
    private _ticketudte_subsctiption: Subscription | null = null;
    private _ticketover_subscription: Subscription | null = null;
    private _ticketload_subscription: Subscription | null = null;
    private _placeview_subscription: Subscription | null = null;

    private _proxy_subscriptions: Array <Subscription> = [];

    private get qrcode(): QrCodeView | null {
        return this.ticket?.qrcode || null;
    }

    readonly DinerPresets = [1, 2, 3, 4, 5];

    get PayAccountName(): string | null {
        const account = this.ticket?.account;
        if (!account){
            return null;
        }

        if (account.name){
            return account.name.trim().toUpperCase();
        }

        if (account.business){
            return account.business.trim().toUpperCase();
        }

        return null;
    }

    get CanDiners(): boolean {
        if (this.IsLocked || this.mode !== 'EDIT' || !this.ticket) {
            return false;
        }
        return this.ticket.CanSetDiners;
    }

    /** True while split/return selection locks the cart against mutations. */
    private _locked = false;
    get IsLocked(): boolean {
        return this._locked;
    }

    set IsLocked(value: boolean){
        if (this._locked === value){
            return;
        }

        this._locked = value;
        this.OnLock.emit(value);

        this.change.markForCheck();
    }

    /**
     * Line/extra/offer edit mode: forced to VIEW while unit selection (split/return) is active.
     */
    get ProductMode(): 'EDIT' | 'VIEW' {
        return this.IsLocked ? 'VIEW' : this.mode;
    }

    /** Header amount to display (due while partial is active, else total). */
    get AmountToPay(): { int: string; dec: string } | null {
        if (!this.ticket){
            return null;
        }

        if (this.ticket.HasPartial){
            const _parts = this.ticket.due.toFixed(2).split('.');
            return { int: _parts[0], dec: _parts[1] };
        }

        return { int: this.ticket.priceinfo.int, dec: this.ticket.priceinfo.dec };
    }

    /** Settled account tickets (recapitulative `invceac`) are read-only; no rectifications. */
    get CanEdit(): boolean {
        if (!this.ticket){
            return false;
        }

        return this.ticket.invceac == null;
    }

    /** Mobile-only: while editing the cart, offers a way back to the catalog without closing it. */
    get CanBack(): boolean {
        return this.mode === 'EDIT' && this.view.Mobile && !this.IsLocked;
    }

    private _showDiners = false;
    get ShowDiners(): boolean {
        return this._showDiners;
    }

    set ShowDiners(value: boolean){
        this._showDiners = value;
        this.change.markForCheck();
    }

    OnDiners(): void {
        this.ShowDiners = !this.ShowDiners;
    }

    SetDinersSize(size: number): void {
        if (!this.ticket){
            return;
        }

        this.ticket.diners = size;
        this.ShowDiners = false;
        this.OnUpdated();
    }

    OnDinersOther(value: string | null): void {
        if (!this.ticket){
            return;
        }
        
        const trimmed = (value || '').trim();
        if (trimmed === ''){
            this.ticket.diners = 0;
            this.OnUpdated();
            return;
        }

        const n = Number(trimmed);
        if (!Number.isInteger(n) || n < 0 || n > 99){
            this.ticket.diners = 0;
            this.OnUpdated();
            return;
        }

        this.ticket.diners = n;
        this.OnUpdated();
    }

    constructor(
        public lang: languageService,
        private change: ViewRenderer,
        public alertCtrl: alertService,
        private data: dataService,
        public toast: toastService,
        public view: viewService,
        public queue: queueService,
        public printer: printService,
    ){
        super(lang, change.cdref);
    }

    ngOnInit(): void {
        this._online_subscription = this.view.OnOnline.subscribe(
        (isOnline: boolean) => {
            this._OnConnectivityChanged(isOnline);
        });
    }

    /********************************/
    /* COMMIT GATE (STALE ORIGINAL) */
    /********************************/

    /**
     * Commit/pay gate against CopyOf only (no live sync / snapshots while the panel is open).
     * Blocks when this working copy is still unpaid but the original was paid while open —
     * Overwrite would persist the ticket as unpaid again. Paid copies (return/reopen) pass.
     * @returns true when OnCommit must abort
     */
    private _CommitBlockedByOriginal(): boolean {
        const _original = (this.ticket?.CopyOf as Ticket) || null;
        if (!_original){
            return false;
        }

        const _staleUnpaid = !this.ticket!.IsPaid && !!_original.PaidFlag;
        if (_staleUnpaid || _original.IsCancelled){
            this.toast.ShowAlert('warning', this.lang.tr('@place_ticket_elsewhere_blocked'), 4000);
            return true;
        }

        return false;
    }

    /**
     * Working-copy identity replace (Overwrite / server Updated on this ticket).
     * Not remote drift detection — rebuilds the panel copy from the new instance.
     */
    private _OnObjectChanged(ticket: Ticket | null){
        if (!ticket){
            return;
        }

        const _args = this.view.PanelArg || {};
        _args['ticket'] = this.data.alives.get(ticket.Copy()) as TicketView;
        _args['mode'] = this.mode;
        this.ToPanel.emit({ args: _args });
    }

    public ChangeUpdate = false;    // works with *ngIf to regenerate the form
    ngOnChanges(changes: SimpleChanges){
        void changes;   // debugging purposes

        if (this.ticket && this.mode === 'EDIT' && !this.CanEdit){
            this.mode = 'VIEW';
        }

        if (this._ticketdata_subscription){
            this._ticketdata_subscription.unsubscribe();
            this._ticketdata_subscription = null;
        }

        if (this._ticketview_subscription){
            this._ticketview_subscription.unsubscribe();
            this._ticketview_subscription = null;
        }

        if (this._ticketudte_subsctiption){
            this._ticketudte_subsctiption.unsubscribe();
            this._ticketudte_subsctiption = null;
        }

        if (this._ticketover_subscription){
            this._ticketover_subscription.unsubscribe();
            this._ticketover_subscription = null;
        }

        if (this._ticketload_subscription){
            this._ticketload_subscription.unsubscribe();
            this._ticketload_subscription = null;
        }

        if (this._placeview_subscription){
            this._placeview_subscription.unsubscribe();
            this._placeview_subscription = null;
        }

        this.ChangeUpdate = true;
        this._ticketcontrol = null;
        this._unitsselection.clear();

        if (this.ticket){
            this._ticketdata_subscription = this.ticket.OnDataRefresh.subscribe(
            () => {
                this._toprepare_products = null;
                this.change.markForCheck();
            });

            this._ticketview_subscription = this.ticket.OnViewRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });

            this._ticketudte_subsctiption = this.ticket.OnTicketUpdated.subscribe(
            () => {
                this._toprepare_products = null;
                this.OnUpdated();
                this.change.markForCheck();
            });

            /* handle target instance modifications */

            this._ticketover_subscription = this.ticket.OnOverwrite.subscribe(
            (ticket: any) => {  // ticket has beeen overwritten: recreate the ticket copy and reload
                this._OnObjectChanged((ticket instanceof Ticket) ? ticket : null);
            });

            this._ticketload_subscription = this.ticket.OnUpdated.subscribe(
            (ticket: any) => {  // ticket has beeen overwritten: recreate the ticket copy and reload
                this._OnObjectChanged((ticket instanceof Ticket) ? ticket : null);
            });
        }

        if (this.place){
            this._placeview_subscription = this.place.OnViewRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }

        setTimeout(() => {
            this.ChangeUpdate = false;
            this.change.markForCheck();
        }, 0);

        this._Enqueue();
        this.OnAction();
    }

    private OnAction(): void {
        const action = this.action;
        if (!action){
            return;
        }

        // Clear PanelArg and run the one-shot after this CD cycle so parent
        // bindings (`[action]="TicketAction"`) do not flip mid-check (NG0100).
        setTimeout(() => {
            const args = this.view.PanelArg;
            if (args && args['action'] != null){
                this.view.PanelArg = { ...args, action: null };
            }

            switch(action){
                case 'send':
                    if (this.control.CanExecute('send')){
                        void this.OnCommit(null);
                    }
                    break;
                case 'cash':
                    if (this.control.CanExecute('cash')){
                        void this.OnCommit('PAYCASH');
                    }
                    break;
                case 'card':
                    if (this.control.CanExecute('card')){
                        void this.OnCommit('PAYCARD');
                    }
                    break;
            }
        }, 0);
    }

    /** Refreshes account-payment affordances when connectivity changes while the panel is open. */
    private _OnConnectivityChanged(isOnline: boolean): void {
        if (!isOnline && this.control.action === 'account'){
            this.control.CancelAction();
        }

        this.change.markForCheck();
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._ticketdata_subscription){
            this._ticketdata_subscription.unsubscribe();
            this._ticketdata_subscription = null;
        }       

        if (this._ticketview_subscription){
            this._ticketview_subscription.unsubscribe();
            this._ticketview_subscription = null;
        }       
        
        if (this._ticketudte_subsctiption){
            this._ticketudte_subsctiption.unsubscribe();
            this._ticketudte_subsctiption = null;
        }

        if (this._ticketover_subscription){
            this._ticketover_subscription.unsubscribe();
            this._ticketover_subscription = null;
        }

        if (this._ticketload_subscription){
            this._ticketload_subscription.unsubscribe();
            this._ticketload_subscription = null;
        }

        if (this._placeview_subscription){
            this._placeview_subscription.unsubscribe();
            this._placeview_subscription = null;
        }

        if (this._online_subscription){
            this._online_subscription.unsubscribe();
            this._online_subscription = null;
        }

        for (const subscription of this._proxy_subscriptions){
            subscription.unsubscribe();
        }
        this._proxy_subscriptions = [];

        this.IsLocked = false;
        this._Dequeue();
    }    
    
    /********************************/
    /* DISPLAY OPTIONS              */
    /********************************/ 

    TicketTrack(index: number, ticket: TicketView): any {
        return ticket?._uuid || null;
    }

    TicketProductTrack = (index: number, ticketproduct: TicketProductView): any => {
        void index;
        return ticketproduct?._hash || null;
    }

    DiscountTrack(index: number, discount: DiscountView): any {
        return discount?._uuid || null;
    }

    AccountTrack(index: number, account: PayAccountView): any {
        void index;
        return account?._uuid || null;
    }

    /********************************/
    /* AVOID DOM MODIFICATIONS      */
    /********************************/ 

    private _toprepare_products: Array <TicketProductView> | null = null; 

    private objectpool = new Map <string, TicketProductView> ();    // keep the displaying ticket products
    private ticketpool = new Map <string, TicketProductView> ();    // keep the retrieved ticket producits

    /**
     * Returns the content-based key used by UI grouping concerns.
     *
     * Scope:
     * - Angular `trackBy` identity for rendered ticket-product rows.
     * - `objectpool` / `_toprepare_products` map keys where lines with the
     *   same content must share the same visual slot.
     *
     * Do not use this key for stable instance lookup because `_hash` changes
     * when editable fields (price, weight, amount, etc.) are modified.
     */
    private TicketProductHashKey(ticketproduct: TicketProductView | null): string | null {
        return ticketproduct?._hash ?? null;
    }

    /**
     * Returns the stable per-instance key used by proxy resolution.
     *
     * Scope:
     * - `ticketpool` lookup identity.
     * - Proxy target resolution in `TicketProductProxy`.
     *
     * This key must remain stable during edits, so it relies on `_uuid`
     * (instance identity) instead of content-derived `_hash`.
     */
    private TicketProductUuidKey(ticketproduct: TicketProductView | null): string | null {
        return ticketproduct?._uuid ?? null;
    }

    /*
        The proxy allows to:
        - regenerate the instance provided to the view (for change detection)
        - but still preserve the same TicketProduct instance
    */

    private TicketProductProxy(trackhash: string, trackid: string, ticketproduct: TicketProductView): TicketProductView {
        return new Proxy(ticketproduct, {
            get: (target, prop) => {
                const targetid = this.TicketProductUuidKey(target as TicketProductView);
                const _ticketproduct: any = (targetid == trackid) ? target : (this.ticketpool.get(trackid) || null);
                if (_ticketproduct){
                    const value = _ticketproduct[prop];
                    if (typeof value === 'function') {
                        return function(...args: any[]) {
                            return value.apply(_ticketproduct, args);
                        };
                    }
                    return value;                    
                } 

                console.error("[PROXY] product not found by hash!", trackhash, trackid);
                return undefined;
            },
            
            set: (target, prop, value) => {
                const targetid = this.TicketProductUuidKey(target as TicketProductView);
                const _ticketproduct: any = (targetid == trackid) ? target : (this.ticketpool.get(trackid) || null);
                if (_ticketproduct){
                    _ticketproduct[prop] = value;
                } 
                return true;
            }
        });
    }

    /*
        we must provide the proxied ticketproducts to the view
        this way the reference can be preserved, on ticketproduct changes
        we'll use the list refresh to perform the map cleanup
    */

    get ToPrepareProducts(): Array <TicketProductView> {
        if (this._toprepare_products === null){
            const _toprepare_products = new Map <string, TicketProductView> ();

            for (const subscription of this._proxy_subscriptions){
                subscription.unsubscribe();
            }
            this._proxy_subscriptions = [];
            
            this.ticketpool.clear();
            if (!this.ticket){
                this.objectpool.clear();
            }
            else {
                for(const ticketproduct of this.ticket.products){
                    if (!ticketproduct.Display || !ticketproduct.InCart){
                        continue;
                    }
                    
                    const trackhash = this.TicketProductHashKey(ticketproduct);
                    const trackid = this.TicketProductUuidKey(ticketproduct);
                    if (!trackhash || !trackid){
                        continue;
                    }
    
                     // keep trackhash during object modifications (required by proxy)
                    this.ticketpool.set(trackid, ticketproduct);
                    let poolproduct = this.objectpool.get(trackhash) || null;
                    if (!poolproduct){
                        poolproduct = this.TicketProductProxy(trackhash, trackid, ticketproduct);
                        this.objectpool.set(trackhash, poolproduct);
                    }
    
                    // new proxy object for same ticketpdoduct (enables changes detection)
                    this._proxy_subscriptions.push(     
                        poolproduct.OnViewRefresh.subscribe(() => {
                            this.objectpool.set(trackhash, this.TicketProductProxy(trackhash, trackid, ticketproduct));
                        })   
                    );
        
                    _toprepare_products.set(trackhash, poolproduct);
                }
    
                 // object pool garbage collector
                 for (const trackhash of this.objectpool.keys()){
                    if (!_toprepare_products.has(trackhash)){
                        this.objectpool.delete(trackhash);
                    }
                }            
            }

            this._toprepare_products = [..._toprepare_products.values()];
        }

        return this._toprepare_products;
    }

    /********************************/
    /* DISPLAY OPTIONS              */
    /********************************/  

    private _contentdirty = false;
    get ContentDirty(): boolean {
        return this._contentdirty;
    }

    set ContentDirty(value: boolean){
        const _previous = this._contentdirty;
        this._contentdirty = value;

        // Keep panel dirty and model dirty in lockstep when the panel flag flips:
        // false→true marks unsent local edits; true→false clears after commit/discard.
        // Model mutations (ToCart, …) may still set ticket.dirty without ContentDirty.
        if (this.ticket){
            if (!_previous && value){
                this.ticket.dirty = true;
            }
            else if (_previous && !value){
                this.ticket.dirty = false;
            }
        }

        this.change.markForCheck();    
    }

    get IsNewTicket(): boolean {
        return !!this.ticket?.IsNewTicket;
    }

    private _tabletickets: Array <TicketView> | null = null;
    get TableTickets(): Array <TicketView> {
        if (!this.qrcode){
            return [];
        }

        if (this._tabletickets === null){
            const _tickets: Array <TicketView> = [];
            for (const ticket of this.qrcode.tickets){
                if (this.ticket && ticket === this.ticket){
                    continue;
                }

                // Drafts (`PR`) stay out of every ticket list until send/payment.
                if (ticket.status !== 'PR' && ticket.IsRecent && ticket.IsOpened){
                    _tickets.push(ticket);
                }
            }

            this._tabletickets = _tickets;
            
            // keep the value during this event loop
            Promise.resolve().then(() => {
                this._tabletickets = null;
            }); 
        } 

        return this._tabletickets
    }

    get StatusColor(): string {
        if (this.ticket){
            if (this.ticket.IsCancelled){
                return 'danger';
            }

            if (this.ticket.IsPaid){
                return 'success';
            }
        }

        return 'dark';
    }

    private _ticketoptions = false;
    get ShowTicketOptions(): boolean {
        return this._ticketoptions;
    }

    set ShowTicketOptions(value: boolean){
        this._ticketoptions = value;
        this.change.markForCheck();
    }

    get CanOptions(): boolean {
        if (!this.ticket || this.IsLocked){
            return false;
        }

        return !this.ticket.IsCancelled;
    }

    /**
     * "Separar comanda" is only offered when the last visible preparation line is not already a separator.
     * (Adding another SP after an SP with no products in between is invalid in the ticket model.)
     */
    get CanSeparate(): boolean {
        const items = this.ToPrepareProducts;
        if (items.length === 0){
            return false;
        }

        return !items[items.length - 1].IsSplitter;
    }

    /********************************/
    /* UNITS SELECTION              */
    /********************************/

    _unitsselection = new Map <string, TicketUnits> ();

    get AvailableUnits(): number {
        let units = 0;
        for (const product of this.ToPrepareProducts){
            if (product.IsSplitter){
                continue;
            }

            units += product.Count;
        }
        return units;
    }

    get SelectedUnits(): number {
        let units = 0;
        for (const selection of this._unitsselection.values()){
            units += Math.max(0, selection.units);
        }
        return units;
    }

    get UnitsSelections(): Array <TicketUnits> {
        const selections: Array <TicketUnits> = [];
        for (const selection of this._unitsselection.values()){
            if (selection.units > 0){
                selections.push(selection);
            }
        }
        return selections;
    }

    OnUnitsSelection(selection: TicketUnits): void {
        const key = selection.product._uuid;
        if (!key){
            throw new Error('[TICKET] Units selection requires product uuid');
        }

        if (selection.units <= 0){
            this._unitsselection.delete(key);
        }
        else {
            const units = Math.min(selection.units, selection.product.Count);
            this._unitsselection.set(key, {
                product: selection.product,
                units
            });
        }

        if (this.control.action === 'return'){
            this.control.OnReturnSelectionChanged();
        }

        this.change.markForCheck();
    }

    /********************************/
    /* EVENT HANDLERS               */
    /********************************/   

    /** When true, the reorder handle is hidden so it does not overlap an open ion-item-sliding strip. */
    private readonly _productsslide = new Map <string, boolean> ();
    IsProductSlider(product: TicketProductView): boolean {
        return this._productsslide.get(product._hash) ?? false;
    }

    OnProductSlider(ev: { hash: string; open: boolean }): void {
        this._productsslide.set(ev.hash, ev.open);
        this.change.markForCheck();
    }

    private comments: string | null = null;
    get Comments(): string | null {
        return this.comments;
    }

    set Comments(value: string | null) {
        this.comments = value;
        this.change.markForCheck();
    }

    OnUpdated(): void {
        this.ContentDirty = true;
    }
           
    OnPanel(panel: PlacePanel): void {
        this.ToPanel.emit(panel);
    }

    OnTicketOptions(): void {
        this.ShowTicketOptions = !this.ShowTicketOptions;
    }

    private EditMode(mode: 'EDIT' | 'VIEW'){
        if (!this.ticket || (mode == this.mode)){
            return;
        }

        if (mode == 'EDIT' && !this.CanEdit){
            return;
        }

        if (mode == 'EDIT'){
            this.ToPanel.emit({
                main: 'cart',
                scnd: 'ticket',
                args: {
                    ticket: this.ticket,
                    mode: mode
                }             
            })
        }
    }

    OnEdit(): void {
        this.EditMode('EDIT');
    }

    /**
     * Navigate back to place view. With `force`, clears dirty and bypasses the cart leave-guard
     * (post-commit exits that must not look like an abandoned draft).
     */
    private _OnClose(force = false): void {
        if (force){
            this.ContentDirty = false;
        }

        this.ToPanel.emit({
            main: "view",
            scnd: null,
            args: null,
            ...(force ? { force: true } : {}),
        });    
    }

    /**
     * Mobile-only navigation back to the product catalog (`upp-cart-select`),
     * staying inside CART mode so the in-progress ticket is not discarded.
     */
    OnBack(): void {
        this.ToPanel.emit({
            main: 'cart',
            scnd: null,
            force: true
        });
    }

    /**
     * Close the ticket panel. Never-product empty drafts are dropped in memory
     * (`CanDiscard`). Empty tickets that once held a product are cancelled
     * and persisted (anti-fraud). Otherwise confirm discard of local changes.
     */
    async OnClose(): Promise <void> {
        if (!this.ticket){
            this._OnClose();
            return;
        }

        if (this.ticket.CanDiscard){
            if (this.ContentDirty){
                const _confirmed = await this._ConfirmCancel();
                if (!_confirmed){
                    return;
                }

                this.ContentDirty = false;
            }

            this.ticket.OnDiscard();
            this._OnClose();
            return;
        }

        const _cancelEmpty = this.ticket.IsEmpty && !this.ticket.IsCancel;

        if (!_cancelEmpty && !this.ContentDirty){
            this._OnClose();
            return;
        }

        const _confirmed = _cancelEmpty ? await this._ConfirmCancel() : await this._ConfirmClose();
        if (!_confirmed){
            return;
        }

        this.ContentDirty = false;

        if (_cancelEmpty || this.IsNewTicket){
            this.ticket.OnCancel();
            this._doCommit(null);
        }

        this._OnClose();
    }

    private get _Continue(): boolean {
        if ((!this.ticket) || (this.ticket.qrcode !== null)){
            return false;
        }

        return !!this.ticket.place?.IsPos;
    }

    private NextTicket(): void {
        if (!this.ticket){
            return;
        }

        const ticket = new Ticket(null, this.data);
        if (!ticket){
            return;
        }

        ticket.created = new Date();
        ticket.updated = new Date();
        ticket.place = this.data.place;
        ticket.qrcode = null;
        ticket.session = this.data.session;
        ticket.price = 0;
        ticket.refund = 0;
        ticket.taxes = 0;
        ticket.status = 'PR';

        // Bypass cart leave-guard: previous ticket was already committed.
        this.ToPanel.emit({
            main: 'cart',
            scnd: 'ticket',
            force: true,
            args: {
                ticket: this.data.alives.get(ticket) as TicketView,
                mode: 'EDIT'
            }
        });
    }

    async OnCommit(payment: TicketPayment | null): Promise <boolean> {
        if (!this.ticket){
            return false;
        }

        // Gate against CopyOf only at commit/pay (no live remote sync while the panel is open).
        if (this._CommitBlockedByOriginal()){
            return false;
        }

        // Send/create uses AllowTicketCommit (license + local backup).
        // Payment uses AllowTicketPayment (connectivity / backup + TicketBAI libs).
        if (payment === null){
            if (!this.place?.AllowTicketCommit){
                return false;
            }
        }
        else if (payment === 'PAYACCOUNT'){
            // Account charges do not require CanPayTickets; license + backup via commit gate.
            if (!this.place?.AllowTicketCommit){
                return false;
            }
        }
        else if (!this.place?.AllowTicketPayment){
            return false;
        }

        let _continue = true;
        if (payment !== null){
            _continue = await this._DoPayTicket(payment);
        }

        if (_continue){
            const _pending = (payment !== null) && this.ticket.ToInvoice;
            if (!_pending) {
                this._toprepare_products = null;
                this.ContentDirty = false;

                this._doCommit(payment !== null ? this.ticket.payment : null);

                if (this._Continue){
                    this.NextTicket();
                }
                else {
                    // Bypass cart leave-guard: this ticket was already sent/paid.
                    this.ToPanel.emit({
                        main: "view",
                        scnd: null,
                        args: null,
                        force: true
                    });
                }
            }
        }

        return _continue;
    }

    CancelAction(): void {
        this.control.CancelAction();
    }

    OnProductDrop(event: CdkDragDrop<TicketProductView[]>, ticketproducts: TicketProductView[]){
        const _prev = event.previousIndex;
        const _next = event.currentIndex;
                
        // place the dropped item 
        const _element = ticketproducts[_prev];
        ticketproducts.splice(_prev, 1);
        ticketproducts.splice(_next, 0, _element);
        
        // reasing the sorting number
        for (let _idx=0; _idx < ticketproducts.length; _idx++){
            ticketproducts[_idx].sort = _idx;
        }

        // apply to non-displayed items
        let _display_fr = _element.DisplayFr;
        while (_display_fr){
            _display_fr.sort = _element.sort;
            _display_fr = _display_fr.DisplayFr;
        }

        this._toprepare_products = null;
        this.change.markForCheck();        
    }

    OnViewTable(){
        if (!this.ticket?.qrcode){
            return;
        }

        this.ToPanel.emit({
            main: 'view',
            scnd: 'table',
            args: {
                table: this.ticket.qrcode
            }
        });
    }

    OnSelectTicket(ticket: TicketView){
        if (!ticket){
            return;
        }

        this.ToPanel.emit({
            main: 'cart',
            scnd: 'ticket',
            args: {
                ticket: this.data.alives.get(ticket.Copy()) as TicketView,
                mode: 'EDIT'
            }
        });        
    }

    OnSeparate(){
        if (!this.ticket){
            return;
        }

        this.ticket.AddSplit();
    }
        
    /********************************/
    /* TICKET CONTROLLER            */
    /********************************/   

    private _ticketcontrol: TicketControl | null = null;
    get control(): TicketControl {
        if (this._ticketcontrol === null){
            this._ticketcontrol = new TicketControl(this.ticket, this.change, this.data, this);
        }
        return this._ticketcontrol;
    }

    /********************************/
    /* CONFIRMATION MODALS          */
    /********************************/

    async _ConfirmClose(): Promise <boolean> {
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@changes_lost_confirm_tittle'),
            message: this.lang.tr('@changes_lost_confirm_message'),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);

                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);

                    return false;
                }
            }]
        });
    
        await alert.present();
        
        const detail = await alert.onDidDismiss();
        if (detail){
            return detail.data;
        }

        return false;
    }    

    async _ConfirmOpen(): Promise <boolean> {
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_ticket_open_confirm'),
            message: this.lang.tr('@place_ticket_open_message'),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });
    
        await alert.present();
        
        const detail = await alert.onDidDismiss();
        if (detail){
            return detail.data;
        }

        return false;
    }

    async _ConfirmCancel(): Promise <boolean> {
        if (!this.ticket){
            return false;
        }

        // Empty with no local edits: discard silently. Otherwise confirm.
        if (this.ticket.IsEmpty && !this.ContentDirty){
            return true;
        }

        return this._ConfirmWithMessage('@place_cancel_ticket_confirm_tittle', '@place_cancel_ticket_confirm_message');
    }   

    async _ConfirmDelete(): Promise <boolean> {
        return this._ConfirmWithMessage('@place_delete_ticket_confirm_tittle', '@place_delete_ticekt_confirm_message');
    }   

    private async _ConfirmWithMessage(headerTrId: string, messageTrId: string): Promise <boolean> {
        const alert = this.alertCtrl.alert({
            header: this.lang.tr(headerTrId),
            message: this.lang.tr(messageTrId),
            backdropDismiss: false,
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });
    
        await alert.present();
        
        const detail = await alert.onDidDismiss();
        if (detail){
            return detail.data;
        }

        return false;
    }
    
    /********************************/
    /* TICKET PAYMENT METHODS       */
    /********************************/

    private _waitmodal = false;

    PayCashTickets: TicketView[] | null = null;
    PayCardTickets: TicketView[] | null = null;

    private PayCashResolve: ((ok: boolean) => void) | null = null;
    private PayCardResolve: ((ok: boolean) => void) | null = null;

    private OnPaycash(): Promise<boolean> {
        if (this._waitmodal || !this.ticket) {
            return Promise.resolve(false);
        }

        this._waitmodal = true;
        this.PayCashTickets = [this.ticket];
        this.change.markForCheck();

        return new Promise((resolve) => {
            this.PayCashResolve = resolve;
        });
    }

    OnPayCashAccept(result: { amount: number | null }): void {
        if (this.ticket && result) {
            this.ticket.given = result.amount;
        }
        this._FinishPayCash(result != null);
    }

    OnPayCashClose(): void {
        this._FinishPayCash(false);
    }

    private _FinishPayCash(ok: boolean): void {
        const resolve = this.PayCashResolve;
        this.PayCashResolve = null;
        this.PayCashTickets = null;

        this._waitmodal = false;
        this.change.markForCheck();

        resolve?.(ok);
    }

    private OnPaycard(): Promise<boolean> {
        if (this._waitmodal || !this.ticket) {
            return Promise.resolve(false);
        }

        this._waitmodal = true;
        this.PayCardTickets = [this.ticket];
        this.change.markForCheck();

        return new Promise((resolve) => {
            this.PayCardResolve = resolve;
        });
    }

    OnPayCardAccept(): void {
        this._FinishPayCard(true);
    }

    OnPayCardClose(): void {
        this._FinishPayCard(false);
    }

    private _FinishPayCard(ok: boolean): void {
        const resolve = this.PayCardResolve;

        this.PayCardResolve = null;
        this.PayCardTickets = null;

        this._waitmodal = false;
        this.change.markForCheck();

        resolve?.(ok);
    }

    private async _DoPayTicket(payment: TicketPayment): Promise <boolean> {
        if (!this.ticket || !this.place){
            return false;
        }

        // Already closed via OnPartial: skip amount modals and do not re-assign payment.
        if (this.ticket.IsPaid){
            return true;
        }

        let _dopay = true;

        if (payment == 'PAYCASH'){
            if (this.place.CashModal){
                _dopay = await this.OnPaycash();
            }
        }

        if (payment == 'PAYCARD'){
            switch (this.place.CardMethod){
                case 'UNLINKED':
                    this.toast.ShowAlert('warning', this.lang.tr('@ticket_cardterm_unlinked'));
                    return false;

                case 'TERMINAL':
                    _dopay = await this.OnPinpadPay();
                    return _dopay;

                default:
                    if (this.place.CardModal){
                        _dopay = await this.OnPaycard();
                    }
                    break;
            }
        }

        if (_dopay){
            await this.ticket.DoPayment(payment);
        }

        return _dopay;
    }

    /********************************/
    /* PINPAD / CARD TERMINAL       */
    /********************************/

    /******** charge ********/

    private async OnPinpadPay(): Promise<boolean> {
        if (this._waitmodal || !this.ticket || !this.place || !this.pinpadSession) {
            return false;
        }

        this._waitmodal = true;
        this.change.markForCheck();
        
        try {
            const _ok = await this.pinpadSession.Charge([this.ticket], this.place);
            if (!_ok) {
                // Same ticket @Input: re-read PE so the unlock banner shows after X / CLOSED.
                this.pinpadTicket?.RefreshNotice();
            }
            return _ok;
        }
        finally {
            this._waitmodal = false;
            this.change.markForCheck();
        }
    }

    /******** pending unlock host ********/

    /** Host reaction when upp-pinpad-ticket successfully ApplyPinpad. */
    OnPinpadTicketApplied(): void {
        this._doCommit('PAYCARD');
    }

    /** Host reaction when upp-pinpad-ticket mutates without sale apply. */
    OnPinpadTicketMutated(): void {
        this.change.markForCheck();
    }

    /********************************/
    /* TICKET PRINTER QUEUE         */
    /********************************/    

    private _queued_ticket: TicketView | null = null;

    /**
     * Starts commit without blocking UI, while keeping a Watch ref until the
     * commit orchestration finishes (so OnOrder/OnPrint still have a consumer
     * after this panel navigates away and Unwatches).
     */
    private _doCommit(payment: TicketPayment | null): void {
        const _ticket = this.ticket;
        if (!_ticket){
            return;
        }

        this.queue.Watch(_ticket);
        
        void _ticket.OnCommit(payment).finally(() => {
            this.queue.Unwatch(_ticket);
        });
    }

    private _Enqueue(): void {
        if (this._queued_ticket === this.ticket){
            return;
        }

        this._Dequeue();
        if (this.ticket){
            this.queue.Watch(this.ticket);
            this._queued_ticket = this.ticket;
        }
    }

    private _Dequeue(): void {
        if (!this._queued_ticket){
            return;
        }

        this.queue.Unwatch(this._queued_ticket);
        this._queued_ticket = null;
    }
}
